import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const ResetPassword: React.FC = () => {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const { confirmPassword: confirmPasswordReset, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email || '';

  // Redirect to forgot password if no email provided
  React.useEffect(() => {
    if (!email) {
      navigate('/forgot-password', { replace: true });
    }
  }, [email, navigate]);

  // Clear errors when form fields change
  React.useEffect(() => {
    clearError();
    setLocalError('');
  }, [code, newPassword, confirmPassword, clearError]);

  const validateForm = (): boolean => {
    if (!code.trim()) {
      setLocalError('Reset code is required');
      return false;
    }

    if (!newPassword.trim()) {
      setLocalError('New password is required');
      return false;
    }

    if (newPassword.length < 12) {
      setLocalError('Password must be at least 12 characters long');
      return false;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setLocalError('Password must include at least one uppercase letter');
      return false;
    }

    if (!/[a-z]/.test(newPassword)) {
      setLocalError('Password must include at least one lowercase letter');
      return false;
    }

    if (!/[0-9]/.test(newPassword)) {
      setLocalError('Password must include at least one number');
      return false;
    }

    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setLocalError('Password must include at least one symbol (e.g., !@#$%)');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setLocalError('');
    clearError();

    try {
      await confirmPasswordReset(email, code.trim(), newPassword);
      navigate('/login', { 
        state: { 
          message: 'Password reset successfully! Please sign in with your new password.' 
        } 
      });
    } catch (error: any) {
      // Error is handled by AuthContext
      if (!authError) {
        setLocalError(error.message || 'Failed to reset password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">🎒</span>
            <h1>Simple Packing</h1>
          </div>
          <h2>Reset Password</h2>
          <p>Enter the reset code sent to <strong>{email}</strong> and your new password.</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {(authError || localError) && (
            <div className="error-message">
              {authError?.message || localError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="code">Reset Code</label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter reset code"
              maxLength={6}
            />
            <small className="form-help">
              Check your email for the reset code
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter new password"
              minLength={8}
            />
            <small className="form-help">
              At least 12 characters with uppercase, lowercase, number, and symbol
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Confirm new password"
            />
          </div>

          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          </p>
          <p>
            Need a new reset code?{' '}
            <Link to="/forgot-password" className="auth-link">
              Request new code
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;